import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useBlogPost } from "@/hooks/useBlogPosts";
import { blogPosts } from "@/mocks/blogPosts";
import { motion } from "motion/react";
import type React from "react";

function renderContent(content: string) {
  const lines = content.split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // H1
    if (trimmed.startsWith("# ") && !trimmed.startsWith("## ")) {
      i++;
      continue; // title already shown in header
    }

    // H2 — section divider with 3-part label detection
    if (trimmed.startsWith("## ")) {
      const heading = trimmed.replace(/^## /, "");
      const isPart = /^[1-9]\ubd80\./.test(heading);
      elements.push(
        <div key={`h2-${i}`} className={`mt-12 mb-5 ${isPart ? "border-t-2 border-[#C4956A]/30 pt-8" : ""}`}>
          {isPart && (
            <span className="inline-block px-3 py-1 text-xs font-semibold bg-[#C4956A]/10 text-[#C4956A] rounded-full mb-3">
              {heading.split(" ").slice(0, 1).join(" ")}
            </span>
          )}
          <h2 className="text-lg md:text-xl font-bold text-[#1C1917] leading-snug">
            {isPart ? heading.split(" ").slice(1).join(" ") : heading}
          </h2>
        </div>
      );
      i++;
      continue;
    }

    // H3 — subsection
    if (trimmed.startsWith("### ")) {
      elements.push(
        <h3 key={`h3-${i}`} className="text-base md:text-lg font-bold text-[#1C1917] mt-8 mb-3 flex items-center gap-2">
          <span className="w-1 h-5 bg-[#C4956A] rounded-full inline-block shrink-0"></span>
          {trimmed.replace(/^### /, "")}
        </h3>
      );
      i++;
      continue;
    }

    // Blockquote
    if (trimmed.startsWith("> ")) {
      const text = trimmed.replace(/^> /, "");
      elements.push(
        <blockquote key={`bq-${i}`} className="my-5 flex gap-3 bg-[#FDF8F3] border border-[#E8DDD3] rounded-xl px-5 py-4">
          <i className="ri-double-quotes-l text-2xl text-[#C4956A] shrink-0 mt-0.5"></i>
          <p className="text-sm text-[#5C5650] leading-relaxed italic">{text}</p>
        </blockquote>
      );
      i++;
      continue;
    }

    // Table — collect all rows
    if (trimmed.startsWith("|")) {
      const tableRows: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableRows.push(lines[i].trim());
        i++;
      }
      // Filter out separator rows (---|---)
      const dataRows = tableRows.filter((r) => !r.replace(/\|/g, "").trim().match(/^[-\s]+$/));
      if (dataRows.length > 0) {
        const headers = dataRows[0].split("|").filter((c) => c.trim() !== "");
        const bodyRows = dataRows.slice(1);
        elements.push(
          <div key={`table-${i}`} className="my-5 overflow-x-auto rounded-xl border border-[#E8E5E0]">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F3F0]">
                <tr>
                  {headers.map((h, hi) => (
                    <th key={hi} className="px-4 py-3 text-left text-xs font-semibold text-[#5C5650] whitespace-nowrap">
                      {h.trim()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, ri) => {
                  const cells = row.split("|").filter((c) => c.trim() !== "");
                  return (
                    <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-[#FAFAF8]"}>
                      {cells.map((cell, ci) => {
                        const boldReplaced = cell.trim().replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#1C1917] font-semibold">$1</strong>');
                        return (
                          <td
                            key={ci}
                            className="px-4 py-3 text-xs text-[#5C5650] border-t border-[#F0EDE8]"
                            dangerouslySetInnerHTML={{ __html: boldReplaced }}
                          />
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    // Horizontal rule — 핵심 요약 section trigger
    if (trimmed === "---") {
      elements.push(<hr key={`hr-${i}`} className="my-10 border-[#E8E5E0]" />);
      i++;
      continue;
    }

    // Unordered list — collect consecutive list items
    if (trimmed.startsWith("- ")) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("- ")) {
        listItems.push(lines[i].trim().replace(/^- /, ""));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="my-3 space-y-2">
          {listItems.map((item, idx) => {
            const boldReplaced = item.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#1C1917] font-semibold">$1</strong>');
            return (
              <li key={idx} className="flex items-start gap-2 text-sm text-[#5C5650] leading-relaxed">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C4956A] shrink-0"></span>
                <span dangerouslySetInnerHTML={{ __html: boldReplaced }} />
              </li>
            );
          })}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(trimmed)) {
      const listItems: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        listItems.push(lines[i].trim().replace(/^\d+\.\s/, ""));
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} className="my-3 space-y-2">
          {listItems.map((item, idx) => {
            const boldReplaced = item.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#1C1917] font-semibold">$1</strong>');
            return (
              <li key={idx} className="flex items-start gap-3 text-sm text-[#5C5650] leading-relaxed">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-[#C4956A]/15 text-[#C4956A] text-xs font-bold flex items-center justify-center shrink-0">
                  {idx + 1}
                </span>
                <span dangerouslySetInnerHTML={{ __html: boldReplaced }} />
              </li>
            );
          })}
        </ol>
      );
      continue;
    }

    // Empty line
    if (trimmed === "") {
      elements.push(<div key={`br-${i}`} className="h-2" />);
      i++;
      continue;
    }

    // Regular paragraph (with bold and link support)
    const boldReplaced = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#1C1917] font-semibold">$1</strong>');
    const linkReplaced = boldReplaced.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" class="text-[#C4956A] underline underline-offset-2 hover:text-[#B08050]">$1</a>'
    );
    elements.push(
      <p
        key={`p-${i}`}
        className="text-sm text-[#5C5650] leading-[1.9] mb-2"
        dangerouslySetInnerHTML={{ __html: linkReplaced }}
      />
    );
    i++;
  }

  return elements;
}

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { post, loading } = useBlogPost(slug || "");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
        <Navbar />
        <main className="pt-24 pb-16 px-4 md:px-6">
          <div className="max-w-[800px] mx-auto text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center animate-pulse">
              <i className="ri-loader-4-line text-2xl text-[#C4956A] animate-spin"></i>
            </div>
            <p className="text-sm text-[#8C8780]">블로그 글을 불러오는 중입니다</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
        <Navbar />
        <main className="pt-24 pb-16 px-4 md:px-6">
          <div className="max-w-[800px] mx-auto text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-[#C4956A]"></i>
            </div>
            <p className="text-sm text-[#8C8780] mb-3">존재하지 않는 글입니다</p>
            <button
              onClick={() => navigate("/blog")}
              className="px-5 py-2 text-xs font-medium text-[#C4956A] border border-[#C4956A]/30 rounded-full hover:bg-[#C4956A]/5 transition-all"
            >
              블로그로 돌아가기
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter((p) => p.slug !== post.slug)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[800px] mx-auto">
          {/* Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-2 text-xs text-[#8C8780] mb-6"
          >
            <Link to="/blog" className="hover:text-[#C4956A] transition-colors">
              블로그
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/blog?category=${post.category}`} className="hover:text-[#C4956A] transition-colors">
              {post.categoryLabel}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#B8B3AE] line-clamp-1">{post.title}</span>
          </motion.nav>

          {/* Article Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="mb-8"
          >
            <span className="inline-block px-3 py-1 text-xs font-medium bg-[#C4956A]/10 text-[#C4956A] rounded-full mb-4">
              {post.categoryLabel}
            </span>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917] mb-4 leading-snug">
              {post.title}
            </h1>
            <p className="text-sm text-[#8C8780] mb-4 leading-relaxed">
              {post.excerpt}
            </p>
            <div className="flex items-center gap-4 text-xs text-[#B8B3AE] pb-6 border-b border-[#E8E5E0]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-[#E8E5E0]">
                  <img loading="lazy" decoding="async" src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover" />
                </div>
                <span className="text-[#5C5650] font-medium">{post.author}</span>
              </div>
              <span>·</span>
              <span>{post.createdAt}</span>
              <span>·</span>
              <span><i className="ri-time-line mr-1"></i>{post.readTime}분</span>
            </div>
          </motion.header>

          {/* Thumbnail */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="mb-8 rounded-2xl overflow-hidden"
          >
            <img loading="lazy" decoding="async"
              src={post.thumbnail}
              alt={post.title}
              className="w-full aspect-[16/9] object-cover"
            />
          </motion.div>

          {/* Article Content */}
          <motion.article
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="bg-white rounded-2xl border border-[#E8E5E0] px-6 md:px-10 py-8 mb-8"
          >
            {renderContent(post.content)}
          </motion.article>

          {/* Tags */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
            className="flex items-center gap-2 flex-wrap mt-8 pt-6 border-t border-[#E8E5E0]"
          >
            {post.tags.map((tag) => (
              <Link
                key={tag}
                to={`/blog?search=${encodeURIComponent(tag)}`}
                className="px-3 py-1.5 text-xs bg-white text-[#5C5650] border border-[#E8E5E0] rounded-full hover:border-[#C4956A]/30 hover:text-[#C4956A] transition-all"
              >
                #{tag}
              </Link>
            ))}
          </motion.div>

          {/* Author Card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.45 }}
            className="mt-8 bg-white rounded-2xl border border-[#E8E5E0] p-6 flex items-center gap-4"
          >
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#E8E5E0] shrink-0">
              <img loading="lazy" decoding="async" src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-[#1C1917]">{post.author}</h4>
              <p className="text-xs text-[#8C8780]">
                {post.author === "강현우"
                  ? "강스터디 대표 강사 · 경매 투자 15년 경력"
                  : post.author === "박지원"
                  ? "강스터디 수석 애널리스트 · 부동산 시장 분석 전문가"
                  : "강스터디 명도 전략 전문 강사"}
              </p>
            </div>
          </motion.div>

          {/* Related Posts */}
          <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-12"
          >
            <h3 className="text-base font-bold text-[#1C1917] mb-5 flex items-center gap-2">
              <i className="ri-link-m text-[#C4956A]"></i>
              관련 글
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedPosts.map((rp) => (
                <Link
                  key={rp.id}
                  to={`/blog/${rp.slug}`}
                  className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden group hover:border-[#C4956A]/30 transition-all"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img loading="lazy" decoding="async"
                      src={rp.thumbnail}
                      alt={rp.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-[10px] text-[#C4956A] font-medium">{rp.categoryLabel}</span>
                    <h4 className="text-xs font-bold text-[#1C1917] mt-1 line-clamp-2 group-hover:text-[#C4956A] transition-colors leading-snug">
                      {rp.title}
                    </h4>
                    <p className="text-[10px] text-[#B8B3AE] mt-2">{rp.createdAt} · {rp.readTime}분</p>
                  </div>
                </Link>
              ))}
            </div>
          </motion.section>

          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-10 text-center"
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-[#1C1917] bg-white border border-[#E8E5E0] rounded-full hover:bg-[#FAFAF8] hover:border-[#C4956A]/30 transition-all"
            >
              <i className="ri-arrow-left-line"></i>
              블로그 목록으로
            </Link>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}